import { useEffect, useState } from 'react';

import { api, getUser } from '../api.js';
import Modal from './Modal.jsx';

const STATUSES = ['offen', 'in Arbeit', 'erledigt'];

/**
 * Modal zum Anlegen eines Ereignisses.
 *
 * Props:
 *  - mandanten: Mandantenliste fürs Dropdown
 *  - defaultMandantId?: optional vorbelegte Mandant-ID
 *  - onClose: () => void
 *  - onSaved: () => Promise|void
 *
 * Autor: Dropdown der bestehenden User (api.listUserOptions). Vorausgewählt
 * ist der aktuell eingeloggte User, kann aber überschrieben werden, falls
 * man stellvertretend einträgt.
 */
export default function EventFormModal({ mandanten, defaultMandantId, onClose, onSaved }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [mandantId, setMandantId] = useState(
        defaultMandantId !== undefined && defaultMandantId !== null
            ? String(defaultMandantId)
            : ''
    );
    const [status, setStatus] = useState('offen');
    const [author, setAuthor] = useState(getUser() || '');
    const [authorOptions, setAuthorOptions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        api.listUserOptions()
            .then((users) => {
                if (cancelled) return;
                setAuthorOptions(users);
                // Falls der eingeloggte User nicht in der Liste auftaucht
                // (sehr unwahrscheinlich), wenigstens als freier Wert mit anbieten.
                const me = getUser();
                if (me && !users.some((u) => u.username === me)) {
                    setAuthorOptions([{ id: -1, username: me }, ...users]);
                }
            })
            .catch(() => { /* unkritisch – Autor bleibt leer wählbar */ });
        return () => { cancelled = true; };
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            await api.createEvent({
                title,
                description: description || null,
                mandant_id: mandantId ? Number(mandantId) : null,
                status,
                author: author || null,
            });
            await onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            title="Neues Ereignis"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn" onClick={onClose}>Abbrechen</button>
                    <button type="submit" form="event-form" className="btn btn--primary" disabled={saving}>
                        {saving ? 'Speichere…' : 'Speichern'}
                    </button>
                </>
            }
        >
            <form id="event-form" onSubmit={handleSubmit}>
                {error && <div className="login__error">{error}</div>}
                <div className="field">
                    <label>Titel *</label>
                    <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div className="grid-2">
                    <div className="field">
                        <label>Mandant</label>
                        <select value={mandantId} onChange={(e) => setMandantId(e.target.value)}>
                            <option value="">– kein Mandant –</option>
                            {mandanten.map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="field">
                        <label>Status</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
                <div className="field">
                    <label>Autor</label>
                    <select value={author} onChange={(e) => setAuthor(e.target.value)}>
                        <option value="">– kein Autor –</option>
                        {authorOptions.map((u) => (
                            <option key={u.id} value={u.username}>{u.username}</option>
                        ))}
                    </select>
                </div>
                <div className="field">
                    <label>Beschreibung</label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
            </form>
        </Modal>
    );
}
